import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, BookOpen, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { searchBible } from "../lib/supabase";
import { searchByTheme } from "../lib/themeSearch";

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "word";

  const handleTabChange = (val) => {
    setSearchParams({ tab: val });
  };

  // --- TAB 1: Mot (Bible - Supabase) ---
  const [wordQuery, setWordQuery] = useState("");
  const [wordResults, setWordResults] = useState([]);
  const [wordLoading, setWordLoading] = useState(false);
  const [wordSearched, setWordSearched] = useState(false);

  const handleWordSearch = useCallback(async () => {
    if (wordQuery.length < 2) return;
    setWordLoading(true);
    setWordSearched(true);
    try {
      const data = await searchBible(wordQuery, 150);
      setWordResults(data.results || []);
    } catch (error) {
      console.error("Search error:", error);
      setWordResults([]);
    } finally {
      setWordLoading(false);
    }
  }, [wordQuery]);

  // --- TAB 2: Thème (Bible - FTS Offline) ---
  const [themeBibleQuery, setThemeBibleQuery] = useState("");
  const [themeBibleResults, setThemeBibleResults] = useState([]);
  const [themeBibleLoading, setThemeBibleLoading] = useState(false);
  const [themeBibleSearched, setThemeBibleSearched] = useState(false);
  const [themeBibleProgress, setThemeBibleProgress] = useState(null);

  const handleThemeBibleSearch = useCallback(async () => {
    if (themeBibleQuery.length < 2) return;
    setThemeBibleLoading(true);
    setThemeBibleSearched(true);
    setThemeBibleProgress(null);
    try {
      const data = await searchByTheme(themeBibleQuery, 150, setThemeBibleProgress, "BIBLE");
      setThemeBibleResults(data || []);
    } catch (error) {
      console.error("Search error:", error);
      setThemeBibleResults([]);
    } finally {
      setThemeBibleLoading(false);
      setThemeBibleProgress(null);
    }
  }, [themeBibleQuery]);

  // --- TAB 3: Thème (Exégèse - FTS Offline) ---
  const [themeQuery, setThemeQuery] = useState("");
  const [themeResults, setThemeResults] = useState([]);
  const [themeLoading, setThemeLoading] = useState(false);
  const [themeSearched, setThemeSearched] = useState(false);
  const [themeProgress, setThemeProgress] = useState(null);

  const handleThemeSearch = useCallback(async () => {
    if (themeQuery.length < 2) return;
    setThemeLoading(true);
    setThemeSearched(true);
    setThemeProgress(null);
    try {
      const data = await searchByTheme(themeQuery, 150, setThemeProgress, "FILLION");
      setThemeResults(data || []);
    } catch (error) {
      console.error("Search error:", error);
      setThemeResults([]);
    } finally {
      setThemeLoading(false);
      setThemeProgress(null);
    }
  }, [themeQuery]);

  const navigateToVerse = (result) => {
    navigate(`/bible/${result.book_id || result.book_name}/${result.chapter}`, {
      state: { scrollToVerse: result.verse },
    });
  };

  const highlightText = (text, start, end) => {
    if (start === undefined || start < 0) return text;
    return (
      <>
        {text.substring(0, start)}
        <mark className="bg-gold-leaf/30 text-ink-primary px-0.5 rounded">
          {text.substring(start, end)}
        </mark>
        {text.substring(end)}
      </>
    );
  };

  const highlightSimple = (text, query) => {
    if (!query || !text) return text;
    const words = query.trim().split(/\s+/).filter(w => w.length >= 2);
    if (words.length === 0) return text;
    
    try {
      const regex = new RegExp(`(${words.join('|')})`, 'gi');
      const parts = text.split(regex);
      return (
        <>
          {parts.map((part, i) => 
            regex.test(part) ? 
              <mark key={i} className="bg-gold-leaf/30 text-ink-primary px-0.5 rounded">{part}</mark> : 
              part
          )}
        </>
      );
    } catch (e) {
      return text;
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="text-center mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl text-ink-primary mb-2">
          Recherche
        </h1>
        <p className="text-sm text-ink-secondary">
          Recherchez un mot exact ou un thème dans la Bible et l'Exégèse
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="word" className="text-xs sm:text-sm font-medium">
            Mot (Bible)
          </TabsTrigger>
          <TabsTrigger value="theme_bible" className="text-xs sm:text-sm font-medium">
            Thème (Bible)
          </TabsTrigger>
          <TabsTrigger value="theme" className="text-xs sm:text-sm font-medium">
            Thème (Exégèse)
          </TabsTrigger>
        </TabsList>

        {/* 1. MOT (BIBLE) */}
        <TabsContent value="word">
          <div className="flex gap-2 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
              <Input
                type="text"
                placeholder="Chercher un mot exact dans la Bible..."
                value={wordQuery}
                onChange={(e) => setWordQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleWordSearch()}
                className="pl-10 h-12 text-base bg-paper border-border focus:border-rubric-red"
              />
            </div>
            <Button
              onClick={handleWordSearch}
              disabled={wordQuery.length < 2 || wordLoading}
              className="h-12 px-6 bg-rubric-red hover:bg-rubric-red/90 text-white"
            >
              {wordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Chercher"}
            </Button>
          </div>

          <div className="flex items-center gap-1.5 mb-6 px-1">
            <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
            <span className="text-xs text-ink-secondary">
              Recherche exacte (requiert une connexion internet)
            </span>
          </div>

          {wordLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
            </div>
          ) : wordSearched && wordResults.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-ink-secondary/20 mx-auto mb-4" />
              <p className="text-ink-secondary">Aucun résultat trouvé pour « {wordQuery} »</p>
            </div>
          ) : wordResults.length > 0 ? (
            <div>
              <p className="text-sm text-ink-secondary mb-4">
                {wordResults.length} résultat{wordResults.length > 1 ? "s" : ""}
              </p>
              <ScrollArea className="h-[calc(100vh-420px)]">
                <div className="space-y-3 pr-4 pb-12">
                  {wordResults.map((result, index) => (
                    <Card
                      key={`word-${index}`}
                      className="border-border bg-paper-dark cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigateToVerse(result)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen className="h-4 w-4 text-rubric-red flex-shrink-0" />
                              <span className="font-medium text-rubric-red text-sm">
                                {result.book_name} {result.chapter}, {result.verse}
                              </span>
                            </div>
                            <p className="text-sm text-ink-primary leading-relaxed line-clamp-3">
                              {highlightText(result.text, result.highlight_start, result.highlight_end)}
                            </p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-ink-secondary flex-shrink-0 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : null}
        </TabsContent>

        {/* 2. THEME (BIBLE) */}
        <TabsContent value="theme_bible">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
              <Input
                type="text"
                placeholder="Saisissez un thème pour la Bible (ex: amour, pardon)..."
                value={themeBibleQuery}
                onChange={(e) => setThemeBibleQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleThemeBibleSearch()}
                className="pl-10 h-12 text-base bg-paper border-border focus:border-rubric-red"
              />
            </div>
            <Button
              onClick={handleThemeBibleSearch}
              disabled={themeBibleQuery.length < 2 || themeBibleLoading}
              className="h-12 px-6 bg-rubric-red hover:bg-rubric-red/90 text-white"
            >
              {themeBibleLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Chercher"}
            </Button>
          </div>

          <div className="flex items-center gap-1.5 mb-6 px-1">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-ink-secondary">
              Recherche large FTS 100% hors-ligne — aucune connexion requise
            </span>
          </div>

          {themeBibleProgress && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
              <p className="text-ink-secondary text-sm">{themeBibleProgress}</p>
            </div>
          )}

          {themeBibleLoading && !themeBibleProgress ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
            </div>
          ) : themeBibleSearched && themeBibleResults.length === 0 && !themeBibleProgress ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-ink-secondary/30 mx-auto mb-4" />
              <p className="text-ink-secondary">Aucun résultat trouvé pour « {themeBibleQuery} »</p>
            </div>
          ) : themeBibleResults.length > 0 ? (
            <div>
              <p className="text-sm text-ink-secondary mb-4">
                {themeBibleResults.length} résultat{themeBibleResults.length > 1 ? "s" : ""}
              </p>
              <ScrollArea className="h-[calc(100vh-420px)]">
                <div className="space-y-3 pr-4 pb-12">
                  {themeBibleResults.map((result, index) => (
                    <Card
                      key={`themeb-${index}`}
                      className="border-border bg-paper-dark cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigateToVerse(result)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full mr-2">
                                Texte Biblique
                              </span>
                              <span className="font-medium text-blue-700 text-sm">
                                {result.reference}
                              </span>
                            </div>
                            <p className="text-sm text-ink-primary leading-relaxed line-clamp-3">
                              {highlightSimple(result.snippet, themeBibleQuery)}
                            </p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-ink-secondary flex-shrink-0 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : null}
        </TabsContent>

        {/* 3. THEME (EXÉGÈSE) */}
        <TabsContent value="theme">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-secondary" />
              <Input
                type="text"
                placeholder="Saisissez un thème (ex: création, rédemption)..."
                value={themeQuery}
                onChange={(e) => setThemeQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleThemeSearch()}
                className="pl-10 h-12 text-base bg-paper border-border focus:border-rubric-red"
              />
            </div>
            <Button
              onClick={handleThemeSearch}
              disabled={themeQuery.length < 2 || themeLoading}
              className="h-12 px-6 bg-rubric-red hover:bg-rubric-red/90 text-white"
            >
              {themeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Chercher"}
            </Button>
          </div>

          <div className="flex items-center gap-1.5 mb-6 px-1">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-ink-secondary">
              Recherche large FTS 100% hors-ligne — aucune connexion requise
            </span>
          </div>

          {themeProgress && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
              <p className="text-ink-secondary text-sm">{themeProgress}</p>
            </div>
          )}

          {themeLoading && !themeProgress ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
            </div>
          ) : themeSearched && themeResults.length === 0 && !themeProgress ? (
            <div className="text-center py-12">
              <Search className="h-12 w-12 text-ink-secondary/30 mx-auto mb-4" />
              <p className="text-ink-secondary">Aucun résultat trouvé pour « {themeQuery} »</p>
            </div>
          ) : themeResults.length > 0 ? (
            <div>
              <p className="text-sm text-ink-secondary mb-4">
                {themeResults.length} résultat{themeResults.length > 1 ? "s" : ""}
              </p>
              <ScrollArea className="h-[calc(100vh-420px)]">
                <div className="space-y-3 pr-4 pb-12">
                  {themeResults.map((result, index) => (
                    <Card
                      key={`theme-${index}`}
                      className="border-border bg-paper-dark cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigateToVerse(result)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-base" role="img" aria-label="Fillion">🖋️</span>
                              <span className="text-xs font-semibold text-rubric-red bg-rubric-red/10 px-2 py-0.5 rounded-full mr-2">
                                Fillion Exégèse
                              </span>
                              <span className="font-medium text-rubric-red text-sm">
                                {result.reference}
                              </span>
                            </div>
                            <p className="text-sm text-ink-secondary leading-relaxed line-clamp-3">
                              {highlightSimple(result.snippet, themeQuery)}
                            </p>
                          </div>
                          <ArrowRight className="h-5 w-5 text-ink-secondary flex-shrink-0 mt-1" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SearchPage;
